"use client";
import {
  Box,
  Checkbox,
  CircularProgress,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon, VeTypography } from "../../../mui";
import InfoImg from "./InfoImg";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { VeInput } from "../../Input/VeInput";
import { VeButton } from "../../Button/button";
import { VeTooltip } from "../../VeTooltip/VeTooltip";
import LinkSvg from "./LinkSvg";
import { areArraysIdentical, limiter } from "../../../hoc";
import { VeDrawer } from "../../VeDrawer/VeDrawer";

interface FileInfo {
  s3ObjectKey: string;
  fileSize: number;
  status: "ACTIVE" | "IN_ACTIVE";
  originalFileName: string;
  commentStatus?: string;
  comment?: string;
  objectKey?: string;
  docType?: string;
  idDocSubType?: string;
  createdAt?: string;
}

const ReferenceDrawer = ({
  open,
  toggleDrawer,
  question,
  upload,
  DynamicImage,
  getFileTypeImage,
  useQueryData,
  useMutation,
  mutateAction,
  toast,
  useSession,
  id,
  projectId,
  updateQuestionMetadata,
  timeZoneDateTimeUser,
  edit = true,
  setProjectDetails,
  projectDetails,
  assets,
  user = false,
}: any) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { data: session, update } = useSession();
  const [links, setLinks] = useState("");
  const [linkChecked, setLinkChecked] = useState<string[]>([]);
  const [checked, setChecked] = useState<string[]>([]);
  const [linkCheckedOriginal, setLinkCheckedOriginal] = useState<string[]>([]);
  const [checkedOriginal, setCheckedOriginal] = useState<string[]>([]);
  const [uploadedChecked, setUploadedChecked] = useState<string[]>([]);
  const [updateStatus, setUpdateStatus] = useState<boolean>(false);
  const { data, isFetching, refetch } = useQueryData({
    queryKey: ["references", id, assets?.projectReviewer],
    endPoint: `/project/v2/onboarding/answer/${id}/references${
      assets?.projectReviewer ? "/reviewer" : ""
    }`,
    enabled: !!open,
    // staleTime: 24 * 60 * 60 * 1000,
  });
  const { data: documentsData, refetch: documentsRefetch } = useQueryData({
    queryKey: ["documents-list-again", projectId],
    endPoint: `/project/description/user/docs?id=${projectId}`,
    // staleTime: 1000 * 60 * 5, // 5 minutes
    // cacheTime: 1000 * 60 * 10, // Cache data for 10 minutes
  });

  const [files, setFiles] = useState<any>([]);
  const [loading, setLoading] = useState<any>(false);

  const { mutate: updateProject } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-project"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      setProjectDetails(data?.updated_project_data);
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate: downloadFile } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["download-file"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      const newWindow = window.open(data?.url);
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["update-answer"],
    onSuccess: (data: any) => {
      if (data.error) {
        throw new Error(data.error?.heading, { cause: data?.error?.message });
      }
      // toast("Reference Updated Successfully", { type: "success" });
      // toggleDrawer(false);
      updateQuestionMetadata(id, {
        docCount: uploadedChecked?.length + checked?.length,
        referenceLinks: [
          ...(links?.length > 1
            ? links
                .split(",")
                .map((item) => item.trim())
                .filter((item) => item.length > 0)
            : []),
          ...linkChecked,
        ],
      });
      if (updateStatus) {
        updateProject({
          type: "patch",
          endPoint: `/project/v2/onboarding/${projectDetails?.id}`,
          data: {
            stepper: {
              ...projectDetails?.stepper,
              documents: "COMPLETED",
            },
          },
          session,
          update,
        });
        setUpdateStatus(false);
      }
      setCheckedOriginal(checked);
      setLinkCheckedOriginal(linkChecked);
      setLinks("");
      refetch();
      setUploadedChecked([]);
      setFiles([]);
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const handleOnSave = () => {
    const addNew = files
      ?.filter((item: FileInfo) => uploadedChecked?.includes(item.s3ObjectKey))
      ?.map((item: FileInfo) => ({
        displayName: item?.originalFileName,
        key: item.s3ObjectKey,
        fileSize: `${item.fileSize}`,
        dateOfDocumentCreated: item.createdAt,
      }));
    if (addNew?.length && data?.documents?.length === 0)
      setProjectDetails &&
        setProjectDetails({
          ...projectDetails,
          stepper: {
            ...projectDetails?.stepper,
            documents: "COMPLETED",
          },
        });
    const addExisting = data?.documents
      ?.filter((item: any) => !item?.answerId && checked?.includes(item?.id))
      ?.map((item: any) => item?.id);

    const deleteDocs = data?.documents
      ?.filter((item: any) => !!item?.answerId && !checked?.includes(item?.id))
      ?.map((item: any) => item?.answerProjectDriveId);

    if (documentsData?.projectDocs?.length === 0 && addNew?.length > 0) {
      setUpdateStatus(true);
    }

    mutate({
      type: "post",
      endPoint: `/project/v2/onboarding/reference`,
      data: {
        referenceLinks: [
          ...(links?.length > 1
            ? links
                .split(",")
                .map((item) => item.trim())
                .filter((item) => item.length > 0)
            : []),
          ...linkChecked,
        ],
        addExisting,
        projectId,
        answerId: id,
        deleteDocs,
        addNew,
      },
      session,
      update,
    });
  };

  useEffect(() => {
    if (data) {
      setChecked(
        data?.documents
          ?.filter((item: any) => !!item?.answerId)
          ?.map((item: any) => item?.id)
      );
      setLinkChecked(data?.referenceLinks?.referenceLinks || []);
      setCheckedOriginal(
        data?.documents
          ?.filter((item: any) => !!item?.answerId)
          ?.map((item: any) => item?.id)
      );
      setLinkCheckedOriginal(data?.referenceLinks?.referenceLinks || []);
    }
  }, [data]);

  const handleCheckBox = (e: any) => {
    if (e.target.checked)
      setChecked((prev: string[]) => [...prev, e.target.value]);
    else
      setChecked((prev: string[]) => {
        const arr = prev?.filter((item: string) => item !== e.target.value);
        return arr;
      });
  };

  const handleUploadedCheckBox = (e: any) => {
    if (e.target.checked)
      setUploadedChecked((prev: string[]) => [...prev, e.target.value]);
    else
      setUploadedChecked((prev: string[]) => {
        const arr = prev?.filter((item: string) => item !== e.target.value);
        return arr;
      });
  };

  const handleLinkCheckBox = (e: any) => {
    if (e.target.checked)
      setLinkChecked((prev: string[]) => [...prev, e.target.value]);
    else
      setLinkChecked((prev: string[]) => {
        const arr = prev?.filter((item: string) => item !== e.target.value);
        return arr;
      });
  };

  const { mutate: uploadOnProj } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["create-project-document"],
    onSuccess: async (response: any) => {
      if (response.error)
        throw new Error(response.error?.heading, {
          cause: response?.error?.message,
        });
    },
    onError: (error: any) => {
      toast(error?.cause as string, { type: "error" });
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) {
      return;
    }
    setLoading(true);

    try {
      const formData = new FormData();
      const fileInfos: FileInfo[] = [];
      Array.from(files).forEach((file: File) => {
        const pattern = /[^a-zA-Z0-9 _.-]/g;
        const fileName = file?.name.replace(pattern, "_");
        const newFile = new File([file], fileName, { type: file.type });
        formData.append("files", newFile);
        if (fileName && file?.size > 0) {
          fileInfos.push({
            s3ObjectKey: file?.name,
            fileSize: file?.size,
            status: "ACTIVE",
            originalFileName: file?.name,
            comment: "",
            commentStatus: "",
            objectKey: "",
          });
        }
      });
      formData.append("bucket", process.env.NEXT_PUBLIC_BUCKET_FILES!);
      const data = await mutateAction({
        data: formData,
        type: "post",
        endPoint: "/files/upload",
        session,
        headersReqWithSession: {
          "Content-Type": `multipart/form-data`,
        },
        update,
      });

      const updatedFileInfos = fileInfos?.map((item, index) => ({
        ...item,
        objectKey: data?.fileNames[index]?.objectKey,
        s3ObjectKey: data?.fileNames[index]?.objectKey,
        createdAt: new Date().toISOString(),
      }));
      // const dataPayload = updatedFileInfos?.map((item: any) => {
      //   return {
      //     projectId,
      //     displayName: item?.originalFileName,
      //     key: item?.s3ObjectKey,
      //     dateOfDocumentCreated: item?.createdAt,
      //     fileSize: item?.fileSize?.toString(),
      //   };
      // });
      if (!data?.error) {
        setFiles((prev: any) => [...prev, ...updatedFileInfos]);
        setUploadedChecked((prev: any) => [
          ...prev,
          ...updatedFileInfos?.map((item) => item.s3ObjectKey),
        ]);
        setLoading(false);
        // uploadOnProj({
        //   type: "post",
        //   endPoint: "/project/description/bulk-docs",
        //   data: {
        //     document: dataPayload,
        //   },
        //   session,
        //   update,
        // });
      } else {
        setLoading(false);
        toast("Can't upload this file", { type: "error" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDiscardChanges = () => {
    setChecked(checkedOriginal);
    setLinkChecked(linkCheckedOriginal);
    setLinks("");
  };

  const isDisabled =
    areArraysIdentical(checked || [], checkedOriginal || []) &&
    areArraysIdentical(linkChecked || [], linkCheckedOriginal || []) &&
    links?.length === 0 &&
    uploadedChecked?.length === 0;

  const handleDownload = (s3key: string) => {
    if (assets?.projectReviewer) {
      if (assets?.downloadFilesReviewer)
        downloadFile({
          type: "get",
          endPoint: `/project/onboarding/files/signed-url?key=${s3key}&bucket=${process
            .env.NEXT_PUBLIC_BUCKET_FILES!}`,
          session,
          update,
        });
    } else
      downloadFile({
        type: "get",
        endPoint: `/project/onboarding/files/signed-url?key=${s3key}&bucket=${process
          .env.NEXT_PUBLIC_BUCKET_FILES!}`,
        session,
        update,
      });
  };

  const fileOpener = (fileName: string, key?: boolean) => {
    const url = user
      ? `${
          window.location.origin
        }/${projectId}/view-document/${encodeURIComponent(fileName)}${
          key ? "?fromKey=true" : ""
        }`
      : `${
          window.location.origin
        }/project-management/projects/${projectId}/view-document/${encodeURIComponent(
          fileName
        )}${key ? "?fromKey=true" : ""}`;
    window.open(url, "_blank"); // '_blank' opens the link in a new tab
  };

  return (
    <VeDrawer
      isOpen={open}
      onClose={() => toggleDrawer(false)}
      style={{ zIndex: 1300 }}
    >
      <Box
        width={isMobile ? 360 : 420}
        paddingTop={"16px"}
        display={"flex"}
        flexDirection={"column"}
        gap={"24px"}
        paddingX={"16px"}
        height={"89vh"}
        overflow="auto"
        paddingBottom={"68px"}
        sx={{ scrollbarWidth: "none" }}
      >
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box display={"flex"} alignItems={"center"} gap={"4px"}>
            <VeTypography variant="label1StrongMain">
              Add Reference
            </VeTypography>
            <VeTooltip
              placement="bottom"
              title={
                <Box
                  display={"flex"}
                  width={"340px"}
                  flexDirection={"column"}
                  padding={"8px"}
                >
                  <VeTypography
                    variant="label4Main"
                    sx={{
                      color: "white !important",
                      fontSize: "12px",
                      lineHeight: "18px",
                    }}
                  >
                    Please ensure that the files are uploaded in one of the
                    following formats: .avi, .mov, .webm, .wmv, .mpeg, .gp,
                    .mkv, .mp4, .flv, .kml, .kmz, .dbf, .shp, .shx, .tif, .jpg,
                    .tiff, .webp, .png, .jpeg, .gif, .bmp, .svg, .heic, .pdf,
                    .xlsm, .csv, .doc, .docx, .md, .ods, .odt, .potx, .pps,
                    .ppsx, .ppt, .rtf, .xls, .xlsx, .xtl, .html, .odp, .txt,
                    .xml, .json, .pptx.
                  </VeTypography>
                </Box>
              }
              arrow
              sx={{ display: "flex", justifyContent: "center", zIndex: 100000 }}
              // color="white"
            >
              <Box
                display={"flex"}
                sx={{ cursor: "help" }}
                width={"fit-content"}
                alignItems={"center"}
                marginLeft={"5px"}
              >
                <InfoImg small />
              </Box>
            </VeTooltip>
          </Box>
          <Box
            display={"flex"}
            alignItems={"center"}
            gap={"4px"}
            onClick={() => toggleDrawer(false)}
            sx={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon
              size="sm"
              height="20px"
              icon={faTimes}
              color="#5F6368"
              style={{ marginBottom: "2px" }}
            />
            <VeTypography variant="label3StrongTertiary">Close</VeTypography>
          </Box>
        </Box>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={"16px"}
          height={"100%"}
          position={"relative"}
        >
          <VeTypography variant="label2Main">{question}</VeTypography>
          {edit && (
            <VeInput
              name="link"
              placeHolder="Enter links separated by commas"
              value={links}
              onChange={(e: any) => setLinks(e.target.value)}
            />
          )}
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={"8px"}
            height={isFetching ? "100%" : ""}
          >
            {edit && (
              <Box display={"flex"} justifyContent={"space-between"}>
                <VeTypography variant="label3Tertiary">
                  Select from existing files (
                  {uploadedChecked?.length +
                    checked?.length +
                    linkChecked?.length}
                  )
                </VeTypography>
                <input
                  type="file"
                  id={"upload"}
                  name={"upload"}
                  hidden
                  onChange={handleFileChange}
                  multiple
                  accept=".avi,.mov,.webm,.wmv,.mpeg,.gp,.mkv,.mp4,.flv,.kml,.kmz,.dbf,.shp,.shx,.tif,.jpg,.tiff,.webp,.png,.jpeg,.gif,.bmp,.svg,.heic,.pdf,.xlsm,.csv,.doc,.docx,.md,.ods,.odt,.potx,.pps,.ppsx,.ppt,.rtf,.xls,.xlsx,.xtl,.html,.odp,.txt,.xml,.json,.pptx"
                />
                <label htmlFor={"upload"}>
                  <Box display={"flex"} gap={"2px"} alignItems={"center"}>
                    {loading ? (
                      <CircularProgress sx={{ color: "var(--vc-violet)" }} size={10} />
                    ) : (
                      DynamicImage(upload, "close", 16, 16)
                    )}
                    <VeTypography
                      sx={{ cursor: "pointer" }}
                      variant="label3StrongBrand"
                      className="document_title"
                    >
                      Upload File
                    </VeTypography>{" "}
                  </Box>
                </label>
              </Box>
            )}
            {isFetching ? (
              <Box
                width={"100%"}
                display="flex"
                justifyContent={"center"}
                alignItems={"center"}
                minHeight={"100%"}
              >
                <CircularProgress sx={{ color: "var(--vc-violet)" }} size={30} />
              </Box>
            ) : (
              <Box display={"flex"} flexDirection={"column"} minHeight={"100%"}>
                {files?.map((item: FileInfo) => (
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    borderBottom={"0.5px solid #E8E8E8"}
                  >
                    <Box display={"flex"} gap={"6px"} alignItems={"center"}>
                      <Checkbox
                        sx={{
                          color: "#D5DBE1",
                          "&.Mui-checked": {
                            color: "var(--vc-violet)",
                          },
                        }}
                        // disabled={drawerOpenedBy === "view"}
                        value={item?.s3ObjectKey}
                        checked={uploadedChecked?.includes(item.s3ObjectKey)}
                        onChange={handleUploadedCheckBox}
                        disabled={!edit}
                      />
                      {DynamicImage(
                        getFileTypeImage(
                          item?.s3ObjectKey?.split(".")[
                            item?.s3ObjectKey?.split(".")?.length - 1
                          ]
                        ),
                        "file",
                        16,
                        16
                      )}
                      <VeTypography
                        variant={
                          uploadedChecked?.includes(item.s3ObjectKey)
                            ? "label3StrongMain"
                            : "label3Secondary"
                        }
                        sx={{ cursor: "pointer" }}
                        // onClick={() => handleDownload(item.s3ObjectKey)}
                        onClick={() => fileOpener(item.s3ObjectKey, true)}
                      >
                        {limiter({
                          strings: item?.originalFileName,
                          limit: 30,
                        })}
                      </VeTypography>
                    </Box>
                    <VeTypography variant="label4Disabled">
                      {timeZoneDateTimeUser({
                        date: item.createdAt,
                        format: "DD-MM-YYYY",
                      })}
                    </VeTypography>
                  </Box>
                ))}
                {data?.referenceLinks?.referenceLinks?.map((item: any) => (
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    borderBottom={"0.5px solid #E8E8E8"}
                  >
                    <Box display={"flex"} gap={"6px"} alignItems={"center"}>
                      <Checkbox
                        sx={{
                          color: "#D5DBE1",
                          "&.Mui-checked": {
                            color: "var(--vc-violet)",
                          },
                        }}
                        // disabled={drawerOpenedBy === "view"}
                        value={item}
                        checked={linkChecked?.includes(item)}
                        onChange={handleLinkCheckBox}
                        disabled={!edit}
                      />
                      <LinkSvg />
                      <a href={item} target="_blank" rel="noopener noreferrer">
                        <VeTypography
                          variant={
                            linkChecked?.includes(item)
                              ? "label3StrongMain"
                              : "label3Secondary"
                          }
                        >
                          {limiter({ strings: item, limit: 30 })}
                        </VeTypography>
                      </a>
                    </Box>
                    {/* <VeTypography variant="label4Disabled">
                    {item.createdAt}
                  </VeTypography> */}
                  </Box>
                ))}
                {data?.documents?.map((item: any) => (
                  <Box
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    borderBottom={"0.5px solid #E8E8E8"}
                  >
                    <Box display={"flex"} gap={"6px"} alignItems={"center"}>
                      <Checkbox
                        sx={{
                          color: "#D5DBE1",
                          "&.Mui-checked": {
                            color: "var(--vc-violet)",
                          },
                        }}
                        // disabled={drawerOpenedBy === "view"}
                        value={item?.id}
                        checked={checked?.includes(item.id)}
                        onChange={handleCheckBox}
                        disabled={!edit}
                      />
                      {DynamicImage(
                        getFileTypeImage(
                          item?.key?.split(".")[
                            item?.key?.split(".")?.length - 1
                          ]
                        ),
                        "file",
                        16,
                        16
                      )}
                      <VeTypography
                        variant={
                          checked?.includes(item.id)
                            ? "label3StrongMain"
                            : "label3Secondary"
                        }
                        sx={{ cursor: "pointer" }}
                        // onClick={() => handleDownload(item.key)}
                        onClick={() => fileOpener(item.id)}
                      >
                        {limiter({ strings: item?.displayName, limit: 30 })}
                      </VeTypography>
                    </Box>
                    <VeTypography variant="label4Disabled">
                      {timeZoneDateTimeUser({
                        date: item.createdAt,
                        format: "DD-MM-YYYY",
                      })}
                    </VeTypography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        {edit && (
          <Box
            display={"flex"}
            gap={"16px"}
            padding={"16px 24px"}
            width={"100%"}
            position={"absolute"}
            right={"0"}
            // marginLeft={"-20px"}
            bottom={"0px"}
            style={{
              background: "white",
              zIndex: 100,
            }}
            boxShadow={
              "0px 2px 8px -2px rgba(0, 0, 0, 0.12), 0px 2px 16px 0px rgba(0, 0, 0, 0.08);"
            }
          >
            <VeButton
              buttonVariant="outlined"
              fullWidth
              disabled={isDisabled}
              buttonClick={handleDiscardChanges}
            >
              Discard Changes
            </VeButton>
            <VeButton
              buttonVariant="contained"
              disabled={isDisabled}
              fullWidth
              buttonClick={handleOnSave}
              loading={isPending}
            >
              Save
            </VeButton>
          </Box>
        )}
      </Box>
    </VeDrawer>
  );
};

export default ReferenceDrawer;
